/**
 * `GET /access/users?can_see=<entityId>` — die Kandidatenliste für
 * `components/AssigneeField.tsx`, geteilt zwischen `task-assignee` (Spec 06a)
 * und `duedate-task-assignee` (Spec 06b, „dieselbe Auswahlkomponente wie für
 * task-assignee, keine zweite", AK-568).
 *
 * Liegt in `lib/`, nicht in einem `features/*\/api.ts`: `AssigneeField` ist
 * geteilte Oberfläche in `components/` (`apps/web/CLAUDE.md`, „Gemeinsames
 * nach components/ oder lib/"), und `components/` darf `features/*` nicht
 * importieren (eslint-boundaries) — dieselbe Begründung wie `lib/entities.ts`
 * für `EntityPicker`. Ursprünglich lag diese Abfrage in
 * `features/tasks/api.ts` (Spec 06a); mit dem zweiten Aufrufer aus Spec 06b
 * zieht sie hierher, aus demselben Grund, aus dem `FormField.tsx` einst nach
 * `components/` zog.
 *
 * Ungefiltert geladen, sobald das Feld öffnet — kein Tipptext, das
 * Auswahlfeld ist ein `<select>` über die volle, kleine Kandidatenliste (~10
 * interne Nutzer plus einzelne Externe). Für einen externen Aufrufer liefert
 * der Endpunkt eine leere Liste („extern entdeckt nichts") — kein
 * Sonderfall hier, das leere `<select>` trägt sich selbst.
 */

import { useQuery } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type AssignableUser = components['schemas']['PersonListItem']

interface AssignableUsersPage {
  readonly items: AssignableUser[]
  readonly next_cursor: string | null
}

interface UseAssignableUsersOptions {
  readonly enabled?: boolean
}

export function useAssignableUsers(entityId: string, options: UseAssignableUsersOptions = {}) {
  return useQuery({
    queryKey: keys.access.assignableUsers(entityId),
    queryFn: ({ signal }) => request<AssignableUsersPage>(`/access/users?can_see=${entityId}`, { signal }),
    enabled: (options.enabled ?? true) && entityId !== '',
  })
}
